import React, { useMemo } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Sparkles, TrendingUp, AlertTriangle, Target, DollarSign, Lightbulb, Layers } from 'lucide-react';

// Helper to extract base plan number
const extractBasePlan = (planNumber) => {
  if (!planNumber) return null;
  const match = planNumber.match(/^([A-Za-z0-9]+)/);
  return match ? match[1] : planNumber;
};

const ExecutiveSummary = ({ data, metrics }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const insights = useMemo(() => {
    if (!data || data.length === 0) return [];

    const insightsList = [];
    const totalRev = metrics.totalRevenue;
    const totalProfit = metrics.totalRevenue - metrics.totalWorkloadCost;
    const avgMargin = metrics.avgMargin || (totalRev > 0 ? (totalProfit / totalRev) * 100 : 0);

    // Group data by base plan number for group-based insights
    const planGroups = {};
    data.forEach(client => {
      const basePlan = extractBasePlan(client.id) || client.id;
      if (!planGroups[basePlan]) {
        planGroups[basePlan] = {
          basePlan,
          totalRevenue: 0,
          totalWorkload: 0,
          planCount: 0,
          manager: client.manager
        };
      }
      planGroups[basePlan].totalRevenue += client.revenue;
      planGroups[basePlan].totalWorkload += client.workloadCost;
      planGroups[basePlan].planCount += 1;
    });

    const groupList = Object.values(planGroups);
    const totalGroups = groupList.length;
    const profitableGroups = groupList.filter(g => g.totalRevenue > g.totalWorkload).length;

    // 1. Portfolio Summary (using plan groups)
    insightsList.push({
      title: 'Portfolio Overview',
      text: `Managing ${totalGroups} plan groups (${data.length} total plans) with ${profitableGroups} profitable groups (${Math.round(profitableGroups/totalGroups*100)}%).`,
      icon: Layers,
      color: 'blue'
    });

    // 2. Revenue Concentration (by plan groups)
    const sortedGroups = [...groupList].sort((a, b) => b.totalRevenue - a.totalRevenue);
    const top20Count = Math.ceil(totalGroups * 0.2);
    const top20Rev = sortedGroups.slice(0, top20Count).reduce((sum, g) => sum + g.totalRevenue, 0);
    const top20Percent = (top20Rev / totalRev) * 100;

    insightsList.push({
      title: 'Revenue Concentration',
      text: `Top ${top20Count} plan groups (${Math.round(top20Count/totalGroups*100)}% of portfolio) generate ${top20Percent.toFixed(0)}% of total revenue.`,
      icon: Target,
      color: 'emerald'
    });

    // 3. Profit Leakage (by plan groups)
    const unprofitableGroups = groupList.filter(g => g.totalRevenue < g.totalWorkload);
    const unprofitableCount = unprofitableGroups.length;
    const unprofitableLoss = unprofitableGroups.reduce((sum, g) => sum + Math.abs(g.totalRevenue - g.totalWorkload), 0);
    
    if (unprofitableCount > 0) {
      insightsList.push({
        title: 'Profit Leakage',
        text: `${unprofitableCount} plan groups are unprofitable, creating a drag of $${(unprofitableLoss/1000).toFixed(0)}k on the bottom line.`,
        icon: AlertTriangle,
        color: 'red'
      });
    } else {
      insightsList.push({
        title: 'Strong Performance',
        text: `All ${totalGroups} plan groups are currently profitable with a healthy average margin of ${avgMargin.toFixed(0)}%.`,
        icon: TrendingUp,
        color: 'emerald'
      });
    }

    // 4. Strategic Recommendation
    let recommendation = "";
    if (avgMargin < 30) {
      recommendation = "Focus on increasing Fee for Service revenue and reducing meeting frequency for 'Service Heavy' plan groups.";
    } else if (avgMargin < 50) {
      recommendation = "Portfolio is healthy. Target high-workload groups for efficiency improvements to boost margins.";
    } else {
      recommendation = "Portfolio health is excellent. Opportunity to expand services to 'Growth Potential' plan groups.";
    }

    insightsList.push({
      title: 'Strategic Recommendation',
      text: recommendation,
      icon: Lightbulb,
      color: 'amber'
    });

    return insightsList;
  }, [data, metrics]);

  return (
    <div className={`p-6 rounded-2xl border transition-colors duration-300 mb-6
      ${isDark ? 'bg-gradient-to-r from-[#1a1a1c] to-[#111113] border-white/10' : 'bg-gradient-to-r from-slate-50 to-white border-slate-200'}`}>
      <div className="flex items-center gap-3 mb-6">
        <div className={`p-2 rounded-lg ${isDark ? 'bg-indigo-500/20' : 'bg-indigo-100'}`}>
          <Sparkles className="w-5 h-5 text-indigo-500" />
        </div>
        <div>
          <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Executive Summary</h2>
          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Key insights based on Plan Groups</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {insights.map((insight) => (
          <div key={insight.title} className={`p-4 rounded-xl border transition-all duration-300 hover:shadow-md
            ${isDark ? 'bg-white/5 border-white/5 hover:bg-white/10' : 'bg-white border-slate-100 hover:border-slate-200'}`}>
            <div className="flex items-start gap-3">
              <div className={`mt-1 p-1.5 rounded-lg flex-shrink-0 
                ${insight.color === 'blue' ? 'bg-blue-500/10 text-blue-500' : 
                  insight.color === 'red' ? 'bg-red-500/10 text-red-500' : 
                  insight.color === 'emerald' ? 'bg-emerald-500/10 text-emerald-500' : 
                  'bg-amber-500/10 text-amber-500'}`}>
                <insight.icon className="w-4 h-4" />
              </div>
              <div>
                <h3 className={`text-sm font-bold mb-1 ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                  {insight.title}
                </h3>
                <p className={`text-xs leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  {insight.text}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExecutiveSummary;
